import { usePermissao } from '@/utils/permissao';
import { Car } from 'lucide-react';
import { EntityIcon } from "@/components/ui/cores";
import { PageHeader } from '@/components/ui/page-header';
import { FormActions } from '@/components/ui/form-actions';
import { Formik, FormikProps } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ClienteService, FichaService, SeguradoraService, UsuarioService } from '../../services';
import { Seguro } from '@/types/Seguro';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Row } from '@/components/grid/row';
import { FormikInput } from '@/components/ui/input';
import { FormikSelect } from '@/components/ui/select';
import { FormikAutoComplete } from '@/components/ui/autocomplete';
import { FormikInputDate, FormikInputNumber } from '@/components/ui/formik-input';
import { FormikTextArea } from '@/components/ui/textarea';
import { TipoSeguroEnum, TipoSeguroValues } from '@/types/enum/TipoSeguro';
import { SectionTitle } from '@/components/ui/section-title';
import SeguroEndossoSection from './SeguroEndossoSection';
import { AnexosPanel } from '@/components/anexos/AnexosPanel';
import { Checkbox } from '@/components/ui/checkbox';
import { Endosso } from '@/types/Endosso';
import { getTenant } from '@/utils/TenantUtils';
import { periodoRenovacao } from '@/utils/renovacao';

export const fichaFormValidationSchema = Yup.object().shape({
    seguradora: Yup.object().required('A seguradora é obrigatória'),
    segurado: Yup.object().required('O segurado é obrigatório'),
    corretor: Yup.object().required('O corretor é obrigatório'),
    descricao: Yup.string().required('A descrição é obrigatório'),
    vigenciaInicial: Yup.date().required('Vigência inicial é obrigatória'),
    vigenciaFinal: Yup.date().required('Vigência final é obrigatória'),
    endossos: Yup.array().of(
        Yup.object().shape({
          tipo: Yup.string().required("O tipo é obrigatório"),
          observacaoEndosso: Yup.string().required("A observação é obrigatória")
        })),
});

const initialValues: Seguro = {
    seguradora: undefined!,
    segurado: undefined!,
    corretor: undefined!,
    tipoSeguro: TipoSeguroEnum.veicular,
    descricao: ''
};

const isoDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Novo seguro a partir de um existente: mantém os dados, vigência de 1 ano a partir do vencimento (ou de hoje), sem endossos
const dadosRenovacao = (seguro: Seguro): Seguro => {
    const [inicio, fim] = periodoRenovacao(seguro.vigenciaFinal);
    return {
        segurado: seguro.segurado,
        seguradora: seguro.seguradora,
        descricao: seguro.descricao,
        corretor: seguro.corretor,
        tipoSeguro: seguro.tipoSeguro,
        premioLiquido: seguro.premioLiquido,
        percentualComissao: seguro.percentualComissao,
        observacao: seguro.observacao,
        placa: seguro.placa,
        chassi: seguro.chassi,
        vigenciaInicial: isoDate(inicio) as any,
        vigenciaFinal: isoDate(fim) as any,
        endossos: [],
        seguroAnterior: seguro.id,
    };
}

type SeguroFormPageParams = {
    
}

const SeguroFormPage: React.FC<SeguroFormPageParams> = () => {
    const somenteLeitura = !usePermissao('SEGURO_WRITE');
    const [ficha, setFicha] = useState<Seguro>(initialValues);
    const [loading, setLoading] = useState<boolean>(false);
    const [indexEndossoEditando, setIndexEndossoEditando] = useState<number|undefined>(undefined);
    const navigate = useNavigate();
    const {codigo} = useParams();
    const [searchParams] = useSearchParams();
    const renovarId = codigo === 'novo' ? searchParams.get('renovar') : null;

    useEffect(() => {
        if (renovarId) {
            setLoading(true);
            FichaService.getByCodigo(renovarId)
              .then(({ data }) => setFicha(dadosRenovacao(data)))
              .catch(error => {
                  console.error(error);
              })
              .finally(() => setLoading(false));
        } else if (codigo && codigo !== 'novo') {
            setLoading(true);
            
            FichaService.getByCodigo(codigo)
              .then(result => {
                  setFicha(result.data);
              })
              .catch(error => {
                  console.error(error);
              })
              .finally(() => setLoading(false));
        }        
    },[codigo, renovarId]);

    const onSave = (fichaDTO: Seguro) => {
        setLoading(true);
        if (codigo && codigo !== 'novo'){
            FichaService.update(fichaDTO, codigo)
            .then(result => {
                toast.success('Seguro alterado com sucesso.');
                navigate(`/${getTenant()}/seguros/${codigo}/resumo`, { replace: true });
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => setLoading(false));
        } else {
            FichaService.create(fichaDTO)
            .then((result: any) => {
                setFicha(result.data);
                toast.success('Seguro cadastrado com sucesso.');
                if (result.data.seguroAnterior && !fichaDTO.seguroAnterior) {
                    toast.info(`O seguro anterior da placa ${result.data.placa} foi marcado como renovado.`);
                }
                navigate(`/${getTenant()}/seguros/${result.data.id}/resumo`, { replace: true });
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => setLoading(false));
        }
    }

    const adicionarEndosso = (formikProps: FormikProps<Seguro>) => {
        var endossos = formikProps.values.endossos || [];
        var novoEndosso: Endosso = {
            tipo: undefined,
            observacaoEndosso: ''
        };
        endossos.push(novoEndosso);        
        formikProps.setFieldValue('endossos', endossos);
        setIndexEndossoEditando(endossos.length-1);
    }

    const removerEndosso = (formikProps: FormikProps<Seguro>, indexRemover: number) => {
        var endossos = formikProps.values.endossos;
        endossos?.splice(indexRemover, 1);  
        formikProps.setFieldValue('endossos', endossos);
    }

    return (
        <div className="space-y-6">
            <PageHeader icon={<EntityIcon entidade="seguro" />} title={somenteLeitura ? 'Seguro' : codigo && codigo !== 'novo' ? 'Editar seguro' : renovarId ? 'Renovar seguro' : 'Novo seguro'} />
            <Card className='p-6 shadow-none'>
                <Formik initialValues={ficha} 
                    validationSchema={fichaFormValidationSchema} 
                    enableReinitialize
                    onSubmit={onSave}>
                    {(formikProps: FormikProps<Seguro>) => {

                    return (
                        <>
                        <fieldset disabled={somenteLeitura} className="min-w-0">
                        <Row>
                            <FormikAutoComplete
                                    onSearch={ClienteService.autoCompleteAtivo}
                                    name="segurado"
                                    label="Segurado"
                                    getOptionLabel={item => item.nome}
                                    getOptionValue={item => item}
                                    getKey={item => item.id}
                                    size={6} />
                            
                            <FormikAutoComplete
                                onSearch={SeguradoraService.autoCompleteAtivo}
                                name="seguradora"
                                label="Seguradora"
                                getOptionLabel={item => `${item.nome}`}
                                getOptionValue={item => item}
                                getKey={item => item.id}
                                size={6}
                            /> 
                        </Row>
                        <Row>
                            <FormikInput name='descricao' label='Descrição' size={6} />
                            <FormikAutoComplete
                                onSearch={UsuarioService.autoCompleteAtivo}
                                name="corretor"
                                label="Corretor"
                                getOptionLabel={item => item.nome && `${item.nome} - ${item.email}`}
                                getOptionValue={item => item}
                                getKey={item => item.id}
                                size={6}
                            /> 
                        </Row>
                        <Row>
                            <FormikSelect 
                                name='tipoSeguro' 
                                itens={TipoSeguroValues} 
                                getOptionLabel={value => value.descricao}
                                getOptionValue={value => value.codigo}
                                label='Tipo'
                                allowEmptySelection={false}
                                size={2} 
                            />
                            <FormikInputDate name='vigenciaInicial' label='Vigência Inicial' size={3} />
                            <FormikInputDate name='vigenciaFinal' label='Vigência Final' size={3} />
                            <FormikInputNumber name='premioLiquido' label='Prêmio Líquido' size={2} prefixLabel='R$' />
                            <FormikInputNumber name='percentualComissao' label='Comissão' size={2} suffixLabel='%' decimalScale={0} />     
                        </Row>
                        <Row>
                            <FormikTextArea
                                name="observacao"
                                label="Observação"
                                size={12}
                                rows={3}
                                />
                        </Row>
                        {codigo && codigo !== 'novo' && (
                            <label className="mb-4 flex items-center gap-2.5 text-sm">
                                <Checkbox checked={!!formikProps.values.renovado} onCheckedChange={v => formikProps.setFieldValue('renovado', v === true)} />
                                Renovado (marque se a renovação foi cadastrada como um novo seguro sem usar a opção Renovar)
                            </label>
                        )}
                        {formikProps.values.tipoSeguro === TipoSeguroEnum.veicular && (
                            <>
                            <SectionTitle icon={<Car />} cor="bg-blue-500/10 text-blue-600 dark:text-blue-400">Veículo</SectionTitle>
                            <Row>
                                <FormikInput name='placa' label='Placa' size={3} maxLength={7} className='uppercase' />
                                <FormikInput name='chassi' label='Chassi' size={3} maxLength={17} className='uppercase' />
                            </Row>
                            </>
                        )}
                        <SeguroEndossoSection formikProps={formikProps} 
                            adicionar={() => adicionarEndosso(formikProps)} 
                            salvar={() => setIndexEndossoEditando(undefined)} 
                            alterar={(index) => setIndexEndossoEditando(index)} 
                            remover={(index) => removerEndosso(formikProps, index)} 
                            loading={false} 
                            indexEditando={indexEndossoEditando} />
                        </fieldset>

                        <AnexosPanel entidade="seguro" registro={codigo && codigo !== 'novo' ? codigo : undefined} embutido somenteLeitura={somenteLeitura} />
                        <FormActions loading={loading} onSave={formikProps.submitForm} somenteLeitura={somenteLeitura} />
                        </>
                    )}}                
                </Formik>
            </Card>            
        </div>
    );
}

export default SeguroFormPage;